import { ItemFromIntention } from '@interfaces/analysis/orders-intentions-for-approval/item-from-intention';
import { ItemFromOrderAnalytical } from '@interfaces/analysis/orders-intentions-for-approval/item-from-order-analytical';
import { ItemFromOrderSynthetic } from '@interfaces/analysis/orders-intentions-for-approval/item-from-order-synthetic';
import { OrdersIntentionsData, OrdersIntentionsDataLists } from '@interfaces/analysis/orders-intentions-for-approval/orders-intentions-data';
import { DadosPii } from '@interfaces/analysis/campaign-pii-data';
import { useLoader } from 'components/loader/loader-context';
import { OrderIntentionsListsProvider, useOrderIntentionsLists } from 'data/context/analysis/orders-intetions-lists-context';
import { HeaderNavegationList } from 'helper/adapter/headerNavigation/header-navegation-adapter';
import { ParseDateToEn } from 'helper/format-date-helper';
import { showErrorAlert, showSuccessAlert, showWaitAlert } from 'helper/show-alert-helper';
import { getEnv } from 'helper/window-helper';
import { UseAlert } from 'hook/alert-hook';
import { UseAuth } from 'hook/auth-hook';
import { OrdersItensionsForApprovalPage } from 'pages/layout/analysis/orders-intentions-for-approval-page';
import ErrorNotAuthorizedPage from 'pages/layout/error/error-not-authorized-page';
import { useState, useEffect } from 'react';
import { RestCall } from 'service/api/rest/rest-call';
import { RestUseCase } from 'service/api/rest/rest-usecase';
import { SelectOptionInterface } from '../../../@types/entity/interface/select-option-interface';
import { hasAdminEquivalentRole } from 'helper/role-helper';

const toBoolean = (value: any) => value === true || value === 'true' || value === 1 || value === '1';

export const OrdersIntensionsForApprovalFactory: React.FC = () => {
  const ENDPOINT_ORDER_INTENTION_APPROVAL = getEnv('REACT_APP_END_POINT_ORDER_INTENTION_APPROVAL');
  const ENDPOINT_ACTIVE_CAMPAIGNS = getEnv('REACT_APP_END_POINT_ACTIVE_CAMPAIGN');

  const { showLoader, hideLoader } = useLoader();
  const { showAlert } = UseAlert();
  const { loggedUserData } = UseAuth();
  const [isGestorOrAdmin, setIsGestorOrAdmin] = useState<boolean>(false);
  const [items, setItems] = useState<(ItemFromOrderAnalytical & ItemFromOrderSynthetic & ItemFromIntention)[]>([]);
  const [meliOptions, setMeliOptions] = useState<SelectOptionInterface[]>();

  const [ordersIntentionsDataLists, setOrdersIntentionsDataLists] = useState<OrdersIntentionsDataLists>();
  const [analyticalDataList, setAnalyticalDataList] = useState<OrdersIntentionsData[]>([]);
  const [syntheticDataList, setSyntheticDataList] = useState<OrdersIntentionsData[]>([]);
  const [activeCampaignDataList, setActiveCampaignDataList] = useState<OrdersIntentionsData[]>([]);

  let dateToday = new Date();
  dateToday.setHours(20, 59, 59, 999);
  let newDate: Date = new Date();
  let previousMonthDate = new Date(newDate.setMonth(dateToday.getMonth() - 1));
  previousMonthDate.setHours(0, 0, 0, 0);
  const [dataInicio, setDataInicio] = useState<string>(ParseDateToEn(previousMonthDate.toString()));
  const [dataFim, setDataFim] = useState<string>(ParseDateToEn(dateToday.toString()));

  const [showScreen, updateShowScreen] = useState<boolean>(false);
  useEffect(() => {
    const roleScreen = HeaderNavegationList.filter((header) => header.navTitle === 'Análise')[0].navItem.filter((item) => item.title === 'Pedidos')[0].role;

    if (loggedUserData?.role) {
      loggedUserData.role.forEach((value) => {
        if (roleScreen.includes(value)) {
          updateShowScreen(true);
        }
        if (hasAdminEquivalentRole(loggedUserData?.role) || loggedUserData?.role.includes('COMPRAS_GESTOR')) {
          setIsGestorOrAdmin(true);
        } else {
          setIsGestorOrAdmin(false);
        }
      });
    }
  }, [loggedUserData]);

  useEffect(() => {
    if (ordersIntentionsDataLists !== null && ordersIntentionsDataLists !== undefined) {
      let listAnalytical: OrdersIntentionsData[] = [];
      let listSynthetic: OrdersIntentionsData[] = [];
      if (ordersIntentionsDataLists.pedidos !== null && ordersIntentionsDataLists.pedidos !== undefined) {
        ordersIntentionsDataLists.pedidos.forEach((data) => {
          switch (data.origem) {
            case 'Analitico': {
              listAnalytical = [...listAnalytical, data];
              break;
            }
            case 'Sintetico': {
              listSynthetic = [...listSynthetic, data];
              break;
            }
            default: {
              break;
            }
          }
        });
      }

      setActiveCampaignDataList(ordersIntentionsDataLists.intencoes);
      setAnalyticalDataList(listAnalytical);
      if (ordersIntentionsDataLists.intencoes !== null && ordersIntentionsDataLists.intencoes !== undefined) {
        setSyntheticDataList(listSynthetic);
      } else {
        setSyntheticDataList([]);
      }
    }
  }, [ordersIntentionsDataLists]);

  useEffect(() => {
    findOrdersIntentionsList(dataInicio, dataFim);
    findMeliOptions();
  }, [])

  const findMeliOptions = async () => {
    await new RestCall(ENDPOINT_ACTIVE_CAMPAIGNS! + '/codigos-meli')
      .Get()
      .then((response) => {
        if (response.success) {
          setMeliOptions(
            (response.data ?? []).map((value: any) => {
              const codigo = value.codigoMeli ?? value.codigo ?? value.value ?? value.id ?? value;
              const descricao = value.descricao ?? value.label;
              return { value: codigo, label: descricao ? `${codigo} - ${descricao}` : String(codigo) };
            })
          );
        } else {
          setMeliOptions([]);
          showErrorAlert(showAlert, 'Erro ao listar códigos MELI');
        }
      })
      .catch(() => {
        setMeliOptions([]);
        showErrorAlert(showAlert, 'Erro ao listar códigos MELI');
      });
  };

  const findOrdersIntentionsList = async (initDate: string, endDate: string) => {
    showLoader();
    await new RestCall(ENDPOINT_ORDER_INTENTION_APPROVAL! + '/pedido-intencao' + `?dataInicio=${initDate}&dataFim=${endDate}`)
      .Get()
      .then((response) => {
        if (response.success) {
          setAnalyticalDataList([]);
          setSyntheticDataList([]);
          setActiveCampaignDataList([]);
          setOrdersIntentionsDataLists({
            pedidos: response.data?.pedidos ?? [],
            intencoes: (response.data?.intencoes ?? []).map((value: OrdersIntentionsData) => ({
              ...value,
              campanhaFull: toBoolean(value.campanhaFull),
            })),
          });
        } else {
          showErrorAlert(showAlert, response.message);
        }
      })
      .catch((error) => {
        showErrorAlert(showAlert, error.message);
      });
    hideLoader();
  };

  async function findOrderItemsAnalytical(numeroPedido: number): Promise<any> {
    showLoader();
    await new RestCall(ENDPOINT_ORDER_INTENTION_APPROVAL! + `/itens-pedido-analitico/${numeroPedido}`)
      .Get()
      .then((response) => {
        if (response.success) {
          setItems((response.data ?? []).map((value: any) => ({ ...value, campanhaFull: toBoolean(value.campanhaFull) })));
        } else {
          showErrorAlert(showAlert, response.errors[0]);
        }
      })
      .catch((error) => {
        showErrorAlert(showAlert, 'Error ao listar itens Analítico');
      });
    hideLoader();
  }

  async function findOrderItemsSynthetic(numeroPedido: number, idSolicitante: number): Promise<any> {
    console.log("idSolicitante: ", idSolicitante);
    showLoader();
    await new RestCall(ENDPOINT_ORDER_INTENTION_APPROVAL! + `/itens-pedido-sintetico/${numeroPedido}/${idSolicitante}`)
      .Get()
      .then((response) => {
        if (response.success) {
          setItems((response.data ?? []).map((value: any) => ({ ...value, campanhaFull: toBoolean(value.campanhaFull) })));
        } else {
          showErrorAlert(showAlert, response.errors[0]);
        }
      })
      .catch((error) => {
        showErrorAlert(showAlert, 'Error ao listar itens Sintético');
      });
    hideLoader();
  }

  async function findIntentionItensCampaign(idIntencao: number): Promise<any> {
    showLoader();
    await new RestCall(ENDPOINT_ORDER_INTENTION_APPROVAL! + `/itens-intencao/${idIntencao}`)
      .Get()
      .then((response) => {
        if (response.success) {
          setItems((response.data ?? []).map((value: any) => ({ ...value, campanhaFull: toBoolean(value.campanhaFull) })));
        } else {
          showErrorAlert(showAlert, response.errors[0]);
        }
      })
      .catch((error) => {
        showErrorAlert(showAlert, 'Error ao listar itens da Inteção de Compra');
      });
    hideLoader();
  }

  async function approveOrderAnalitycal(items: ItemFromOrderAnalytical[], obs: string): Promise<any> {
    showLoader();
    await new RestCall(ENDPOINT_ORDER_INTENTION_APPROVAL! + `/aprova-pedido-analitico`)
      .Patch({
        data: {
          itens: items,
          observacao: obs,
        },
      })
      .then((response) => {
        if (response.success) {
          showSuccessAlert(showAlert, response.message);
          findOrdersIntentionsList(dataInicio, dataFim);
        } else {
          showErrorAlert(showAlert, response.errors[0]);
          hideLoader();
        }
      })
      .catch((error) => {
        showErrorAlert(showAlert, 'Error ao aprovar pedido');
        hideLoader();
      });
  }

  async function approveOrderSynthetic(items: ItemFromOrderSynthetic[], obs: string): Promise<any> {
    showLoader();
    await new RestCall(ENDPOINT_ORDER_INTENTION_APPROVAL! + `/aprova-pedido-sintetico`)
      .Patch({
        data: {
          itens: items,
          observacao: obs,
        },
      })
      .then((response) => {
        if (response.success) {
          showSuccessAlert(showAlert, response.message);
          findOrdersIntentionsList(dataInicio, dataFim);
        } else {
          showErrorAlert(showAlert, response.errors[0]);
          hideLoader();
        }
      })
      .catch((error) => {
        showErrorAlert(showAlert, 'Error ao aprovar pedido');
        hideLoader();
      });
  }

  async function approveIntentionCampaign(items: ItemFromIntention[], obs: string, dadosPii?: DadosPii): Promise<any> {
    showLoader();
    await new RestCall(ENDPOINT_ORDER_INTENTION_APPROVAL! + `/aprova-intencao-compra`)
      .Patch({
        data: {
          itens: items,
          observacao: obs,
          ...(dadosPii !== undefined ? { dadosPii } : {}),
        },
      })
      .then((response) => {
        if (response.success) {
          showSuccessAlert(showAlert, response.message);
          findOrdersIntentionsList(dataInicio, dataFim);
        } else {
          showErrorAlert(showAlert, response.errors[0]);
          hideLoader();
        }
      })
      .catch((error) => {
        showErrorAlert(showAlert, 'Error ao aprovar intencao');
        hideLoader();
      });
  }

  async function rejectOrderAnalitycal(items: ItemFromOrderAnalytical[], obs: string): Promise<any> {
    showLoader();
    await new RestCall(ENDPOINT_ORDER_INTENTION_APPROVAL! + `/reprovar-pedido-analitico`)
      .Patch({
        data: {
          itens: items,
          observacao: obs,
        },
      })
      .then((response) => {
        if (response.success) {
          showSuccessAlert(showAlert, response.message);
          findOrdersIntentionsList(dataInicio, dataFim);
        } else {
          showErrorAlert(showAlert, response.errors[0]);
          hideLoader();
        }
      })
      .catch((error) => {
        showErrorAlert(showAlert, 'Error ao aprovar intencao');
        hideLoader();
      });
  }

  async function rejectOrderSynthetic(items: ItemFromOrderSynthetic[], obs: string): Promise<any> {
    showLoader();
    await new RestCall(ENDPOINT_ORDER_INTENTION_APPROVAL! + `/reprovar-pedido-sintetico`)
      .Patch({
        data: {
          itens: items,
          observacao: obs,
        },
      })
      .then((response) => {
        if (response.success) {
          showSuccessAlert(showAlert, response.message);
          findOrdersIntentionsList(dataInicio, dataFim);
        } else {
          showErrorAlert(showAlert, response.errors[0]);
          hideLoader();
        }
      })
      .catch((error) => {
        showErrorAlert(showAlert, 'Error ao aprovar intencao');
        hideLoader();
      });
  }

  async function rejectIntentionCampaign(idIntencao: number, obs: string): Promise<any> {
    showLoader();
    await new RestCall(ENDPOINT_ORDER_INTENTION_APPROVAL! + `/reprovar-intencao-compra`)
      .Patch({
        data: {
          idIntencao: idIntencao,
          observacao: obs,
        },
      })
      .then((response) => {
        if (response.success) {
          showSuccessAlert(showAlert, response.message);
          findOrdersIntentionsList(dataInicio, dataFim);
        } else {
          showErrorAlert(showAlert, response.errors[0]);
          hideLoader();
        }
      })
      .catch((error) => {
        showErrorAlert(showAlert, 'Error ao aprovar intencao');
        hideLoader();
      });
  }

  async function downloadOrderAnalytical(orderNumber: number): Promise<any> {
    showLoader();
    showWaitAlert(showAlert, 'Aguarde um momento');
    let response = await new RestUseCase(ENDPOINT_ORDER_INTENTION_APPROVAL! + `/download-pedido-analitico?numeroPedido=${orderNumber}`).Get();
    if (response.statusCode !== 500) {
      const url = window.URL.createObjectURL(new Blob([response.body], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Pedido_${orderNumber}.txt`);
      document.body.appendChild(link);
      link.click();
      link!.parentNode!.removeChild(link);
      showSuccessAlert(showAlert, 'Pedido gerado com sucesso');
    } else showErrorAlert(showAlert, 'Ops!!! Algo deu errado tente novamente mais tarde...');
    hideLoader();
  }
  async function downloadOrderSynthetic(orderNumber: number): Promise<any> {
    showLoader();
    showWaitAlert(showAlert, 'Aguarde um momento');
    let response = await new RestUseCase(ENDPOINT_ORDER_INTENTION_APPROVAL! + `/download-pedido-sintetico?numeroPedido=${orderNumber}`).Get();
    if (response.statusCode !== 500) {
      const url = window.URL.createObjectURL(new Blob([response.body], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Pedido_${orderNumber}.txt`);
      document.body.appendChild(link);
      link.click();
      link!.parentNode!.removeChild(link);
      showSuccessAlert(showAlert, 'Pedido gerado com sucesso');
    } else showErrorAlert(showAlert, 'Ops!!! Algo deu errado tente novamente mais tarde...');
    hideLoader();
  }

  async function downloadIntention(idIntencao: number): Promise<any> {
    showLoader();
    showWaitAlert(showAlert, 'Aguarde um momento');
    let response = await new RestUseCase(ENDPOINT_ORDER_INTENTION_APPROVAL! + `/download-intencao-compra?idIntencao=${idIntencao}`).Get();
    if (response.statusCode !== 500) {
      const url = window.URL.createObjectURL(new Blob([response.body], { type: 'text/csv' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Intencao_Compra_${idIntencao}.txt`);
      document.body.appendChild(link);
      link.click();
      link!.parentNode!.removeChild(link);
      showSuccessAlert(showAlert, 'Arquivo Intenção Compra gerado com sucesso');
    } else showErrorAlert(showAlert, 'Ops!!! Algo deu errado tente novamente mais tarde...');
    hideLoader();
  }

  return (
    <>
      {showScreen ? (
        <OrderIntentionsListsProvider
          isGestorOrAdmin={isGestorOrAdmin}
          setIsGestorOrAdmin={setIsGestorOrAdmin}
          setItems={setItems}
          items={items}
          dataInicio={dataInicio}
          setDataInicio={setDataInicio}
          dataFim={dataFim}
          setDataFim={setDataFim}
          meliOptions={meliOptions}
          approveOrderAnalitycal={approveOrderAnalitycal}
          approveOrderSynthetic={approveOrderSynthetic}
          approveIntentionCampaign={approveIntentionCampaign}
          rejectOrderAnalitycal={rejectOrderAnalitycal}
          rejectOrderSynthetic={rejectOrderSynthetic}
          rejectIntentionCampaign={rejectIntentionCampaign}
        >
          <OrdersItensionsForApprovalPage
            findOrdersIntentionsList={findOrdersIntentionsList}
            findOrderItemsAnalytical={findOrderItemsAnalytical}
            findOrderItemsSynthetic={findOrderItemsSynthetic}
            findIntentionItensCampaign={findIntentionItensCampaign}
            downloadOrderAnalytical={downloadOrderAnalytical}
            downloadOrderSynthetic={downloadOrderSynthetic}
            downloadIntention={downloadIntention}
            analyticalDataList={analyticalDataList}
            syntheticDataList={syntheticDataList}
            activeCampaignDataList={activeCampaignDataList}
          />
        </OrderIntentionsListsProvider>
      ) : (
        <ErrorNotAuthorizedPage />
      )}
    </>
  );
};
